import { useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';
import { createPortal } from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';

import type { IntegerAmount } from '@actual-app/core/shared/util';

import { useEnvelopeSheetValue } from '#components/budget/envelope/EnvelopeBudgetComponents';
import { useDatosObjetivos } from '#components/mobile/budget/useDatosObjetivos';
import { Bocadillo } from '#components/mobile/travesia/Bocadillo';
import { Destellos } from '#components/mobile/travesia/Destellos';
import type { IdsTravesia } from '#components/mobile/travesia/estado';
import type { GaviEstado } from '#components/mobile/travesia/Gavi';
import {
  cofresDeTravesia,
  mensajeReparto,
  textoAuto,
  textoReparto,
} from '#components/mobile/travesia/reparto';
import type { Asignado } from '#components/mobile/travesia/reparto';
import { useTrato } from '#components/mobile/travesia/trato';
import { ATRIBUTO_FOCO } from '#components/mobile/travesia/TravesiaContext';
import { Boton } from '#components/mobile/ui/Boton';
import { color } from '#components/mobile/ui/tokens';
import { useCategories } from '#hooks/useCategories';
import { envelopeBudget } from '#spreadsheet/bindings';

import { useEuros, useTecladoAbierto, Z_TRAVESIA } from './comun';

type RepartoPlanProps = {
  /** Mes en curso `YYYY-MM`. */
  mes: string;
  ids: IdsTravesia | undefined;
  /** `useEscribirTravesia().repartirAuto`. */
  repartirAuto: (
    toBudget: IntegerAmount,
    asignado: Asignado,
  ) => Promise<unknown>;
  /** El «Auto» del teclado (contexto) llama a lo que haya aquí. */
  autoRef: MutableRefObject<(() => Promise<void>) | null>;
  onAutoPulsa: (pulsa: boolean) => void;
  ocupado?: boolean;
  onContinuar: () => void;
};

type Gesto = { texto: string | null; estado: GaviEstado; clave: number };

/**
 * Paso 7 · «Repartir hasta 0» sobre el Plan real y su teclado. Gavi flota
 * encima de la barra y reacciona a cada cambio de «listos para asignar»
 * (`envelopeBudget.toBudget`, dentro del `SheetNameProvider` del mes): de
 * más, sigue, se aburre (y «Auto» late) o cero (aplaude y destellos). Con el
 * teclado abierto se esconde. Debe ir dentro de `SheetNameProvider`.
 */
export function RepartoPlan({
  mes,
  ids,
  repartirAuto,
  autoRef,
  onAutoPulsa,
  ocupado,
  onContinuar,
}: RepartoPlanProps) {
  const { t } = useTranslation();
  const { eur } = useEuros();
  const { contexto } = useTrato();
  const valor = useEnvelopeSheetValue(envelopeBudget.toBudget);
  const r = typeof valor === 'number' ? valor : null;

  const { data } = useCategories();
  const cofres = cofresDeTravesia(ids);
  const idsCofres = cofres.map(c => c.id);
  const categorias = (data?.list ?? []).filter(c => idsCofres.includes(c.id));
  const datos = useDatosObjetivos(mes, categorias);

  const [gesto, setGesto] = useState<Gesto>({
    texto: null,
    estado: 'vuela',
    clave: 0,
  });
  const [disparo, setDisparo] = useState(0);
  const [foco, setFoco] = useState(true);
  const teclado = useTecladoAbierto();
  const previo = useRef<IntegerAmount | null>(null);
  const cambios = useRef(0);
  const autoEnCurso = useRef<{ habia: boolean } | null>(null);

  // Al abrir el teclado (se ha tocado un cofre) se apaga el foco.
  useEffect(() => {
    if (teclado) {
      setFoco(false);
    }
  }, [teclado]);

  // Gavi reacciona a cada cambio de «listos para asignar».
  useEffect(() => {
    if (r === null) {
      return;
    }
    const antes = previo.current;
    previo.current = r;
    if (antes === null || antes === r) {
      return;
    }
    const enCurso = autoEnCurso.current;
    if (enCurso) {
      autoEnCurso.current = null;
      setGesto(g => ({
        texto: textoAuto(t, enCurso.habia),
        estado: 'aplaude',
        clave: g.clave + 1,
      }));
      if (r === 0) {
        setDisparo(d => d + 1);
      }
      onAutoPulsa(false);
      return;
    }
    cambios.current += 1;
    const m = mensajeReparto(antes, r, cambios.current);
    setGesto(g => ({
      texto: textoReparto(t, m, eur(m.importe), contexto),
      estado: m.gavi,
      clave: g.clave + 1,
    }));
    if (m.tipo === 'cero') {
      setDisparo(d => d + 1);
    }
    onAutoPulsa(m.autoPulsa);
    // Solo cuando cambia el valor.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [r]);

  // El «Auto» del teclado llama a esto (se renueva en cada render).
  useEffect(() => {
    autoRef.current = auto;
  });

  async function auto() {
    const ahora = r ?? 0;
    const asignado: Record<string, IntegerAmount> = {};
    for (const id of idsCofres) {
      asignado[id] = datos.get(id)?.budgeted ?? 0;
    }
    setFoco(false);
    if (ahora === 0) {
      // Nada que repartir: no cambia el valor, Gavi lo dice ya.
      setGesto(g => ({
        texto: textoAuto(t, false),
        estado: 'aplaude',
        clave: g.clave + 1,
      }));
      setDisparo(d => d + 1);
      onAutoPulsa(false);
      return;
    }
    autoEnCurso.current = { habia: true };
    await repartirAuto(ahora, asignado);
  }

  const cero = r === 0;
  const texto =
    gesto.texto ??
    t(
      'This money is loose on deck. Tap a chest and put money in until "ready to assign" shows 0.',
    );

  return createPortal(
    <>
      {foco && !teclado && (
        <style>{`[${ATRIBUTO_FOCO}]{position:relative;z-index:16;border-radius:4px;box-shadow:0 0 0 3px ${color.accent},0 0 0 9999px ${color.velo}}`}</style>
      )}
      <ChispasListo disparo={disparo} />
      {!teclado && (
        <div
          data-testid="gavi-reparto"
          style={{
            position: 'fixed',
            left: 12,
            right: 12,
            bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))',
            zIndex: Z_TRAVESIA.flota,
          }}
        >
          <Bocadillo
            texto={texto}
            estado={gesto.estado}
            clave={gesto.clave}
            data-testid="bocadillo-reparto"
          >
            {cero && (
              <div className="acciones">
                <Boton
                  bloque
                  isDisabled={ocupado}
                  data-testid="travesia-continuar-reparto"
                  onPress={onContinuar}
                >
                  <Trans>Continue</Trans>
                </Boton>
              </div>
            )}
          </Bocadillo>
        </div>
      )}
    </>,
    document.body,
  );
}

/** Destellos sobre la píldora «listos para asignar» del Plan. */
function ChispasListo({ disparo }: { disparo: number }) {
  const [caja, setCaja] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!disparo) {
      return;
    }
    const pildora = document.querySelector('[data-testid="to-budget"]');
    setCaja(pildora?.getBoundingClientRect() ?? null);
  }, [disparo]);
  if (!disparo || !caja) {
    return null;
  }
  return (
    <span
      aria-hidden
      style={{
        position: 'fixed',
        left: caja.left,
        top: caja.top,
        width: caja.width,
        height: caja.height,
        pointerEvents: 'none',
        zIndex: Z_TRAVESIA.flota,
      }}
    >
      <Destellos disparo={disparo} />
    </span>
  );
}
